import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { resolveQuotaKeyDisplayLabel } from '@/helpers/organization-subscription-pricing';
import type { SubscriptionCheckoutCartTotals } from '@/state/queries/transactions/use-subscription-checkout-cart';
import type { TranslationKey } from '@/i18n/translation-key';

/** Active org subscription row (embedded instance), not the checkout draft. */
export type OrganizationBookedPlanSummary = {
  tier: string;
  subscriptionProductKey: string;
  billingPeriod?: string;
};

/** Subscription checkout totals block (same layout idea as event catalog pricing display). */
export default function OrganizationSubscriptionPricingDisplay({
  totals,
  latestOrderBillingCountry,
  vatCountryDisplay,
  loading = false,
  /** Subscription already on the organization — shown as “Current plan”, distinct from cart upgrade lines. */
  bookedPlan,
  showBookedPlan = true,
  showPlanLineBreakdown = true,
}: {
  totals: SubscriptionCheckoutCartTotals;
  latestOrderBillingCountry: string | undefined;
  vatCountryDisplay: string;
  loading?: boolean;
  bookedPlan?: OrganizationBookedPlanSummary | null;
  showBookedPlan?: boolean;
  showPlanLineBreakdown?: boolean;
}) {
  const t = useTranslations();
  const displayVatCalculation = !!latestOrderBillingCountry;
  const planBreakdown = totals.planLines?.length ? totals.planLines : undefined;

  const bookedBillingSuffix =
    bookedPlan?.billingPeriod === 'YEAR'
      ? ` — ${t('subscription.organization.planPage.billedAnnually')}`
      : bookedPlan?.billingPeriod === 'MONTH'
        ? ` — ${t('subscription.organization.planPage.billedMonthly')}`
        : '';
  const legacyPlanSingle =
    !planBreakdown && typeof totals.planChangeAmountEur === 'number'
      ? [
          {
            amountEur: totals.planChangeAmountEur,
            label: t('subscription.organization.planPage.subscriptionCheckoutPlanLine'),
          },
        ]
      : undefined;

  const showBookedSection = showBookedPlan && !!bookedPlan;
  const showPlanLinesSection = showPlanLineBreakdown && !!planBreakdown;
  const showLegacyPlanSection = showPlanLineBreakdown && !!legacyPlanSingle;

  const hasSummaryBeforeTotal =
    showBookedSection || showPlanLinesSection || showLegacyPlanSection || !!(totals.addonLines?.length ?? 0);

  return (
    <>
      {showBookedSection ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('subscription.organization.planPage.currentPlan')}
          </Typography>
          <Box display="flex" justifyContent="space-between" gap={2} sx={{ mt: 0.75 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap title={bookedPlan.subscriptionProductKey}>
                {t(`subscription.organization.tiers.${bookedPlan.tier}` as TranslationKey)}
                {bookedBillingSuffix}{' '}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  ({bookedPlan.subscriptionProductKey})
                </Typography>
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : null}

      {showPlanLinesSection ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('subscription.organization.planPage.subscriptionCheckoutLinesHeading')}
          </Typography>
          {planBreakdown!.map((row, i) => (
            <Box
              key={`${row.subscriptionProductKey}-${i}`}
              display="flex"
              justifyContent="space-between"
              gap={2}
              sx={{ mt: 0.75 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap title={row.subscriptionProductKey}>
                  {t(`subscription.organization.tiers.${row.tier}` as TranslationKey)}{' '}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    ({row.subscriptionProductKey})
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('subscription.organization.planPage.subscriptionCheckoutProrataLabel')}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
                €{row.amountEur.toFixed(2)}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : showLegacyPlanSection ? (
        <Box sx={{ mb: 1.5 }}>
          {legacyPlanSingle!.map((row, i) => (
            <Box key={i} display="flex" justifyContent="space-between" gap={2}>
              <Typography variant="body2">{row.label}</Typography>
              <Typography variant="body2" fontWeight={600}>
                €{row.amountEur.toFixed(2)}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      {totals.addonLines?.length ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('subscription.organization.planPage.subscriptionCheckoutAddons')}
          </Typography>
          {totals.addonLines.map((a) => (
            <Box
              key={`${a.quotaKey}-${a.quantity}`}
              display="flex"
              justifyContent="space-between"
              gap={2}
              sx={{ mt: 0.75 }}
            >
              <Typography variant="body2">
                {resolveQuotaKeyDisplayLabel(t, a.quotaKey)} × {a.quantity}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                €{a.amountEur.toFixed(2)}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      {hasSummaryBeforeTotal && <Divider sx={{ my: 1 }} />}

      {displayVatCalculation && totals.vatRate > 0 ? (
        <>
          <Box display="flex" justifyContent="space-between" sx={{ mt: hasSummaryBeforeTotal ? 0 : 'auto' }}>
            <Typography variant="body2">{t('event.checkoutModal.subtotalExcludingVat')}</Typography>
            {loading ? (
              <CircularProgress size={16} />
            ) : (
              <Typography variant="body2">€{totals.totalEur.toFixed(2)}</Typography>
            )}
          </Box>
          <Box display="flex" justifyContent="space-between" borderBottom={1} borderColor="divider">
            <Typography variant="body2">
              {t('event.checkoutModal.vat', {
                vat: totals.vatRate.toFixed(2),
                country: vatCountryDisplay,
              })}
            </Typography>
            {loading ? (
              <CircularProgress size={16} />
            ) : (
              <Typography variant="body2">€{(totals.totalEur * (totals.vatRate / 100)).toFixed(2)}</Typography>
            )}
          </Box>
          <Box display="flex" justifyContent="space-between" sx={{ pb: 2 }}>
            <Typography variant="body2" fontWeight={900}>
              {t('subscription.organization.planPage.subscriptionCheckoutTotal')}
            </Typography>
            {loading ? (
              <CircularProgress size={16} />
            ) : (
              <Typography variant="body2" fontWeight={900}>
                €{(totals.totalEur * (1 + totals.vatRate / 100)).toFixed(2)}
              </Typography>
            )}
          </Box>
        </>
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" sx={{ mt: hasSummaryBeforeTotal ? 0 : 'auto' }}>
            <Typography variant="body2" sx={{ fontWeight: !displayVatCalculation ? 900 : undefined }}>
              {t('subscription.organization.planPage.subscriptionCheckoutTotal')}
            </Typography>
            {loading ? (
              <CircularProgress size={16} />
            ) : (
              <Typography variant="body2" sx={{ fontWeight: !displayVatCalculation ? 900 : undefined }}>
                €{totals.totalEur.toFixed(2)}
              </Typography>
            )}
          </Box>
          {displayVatCalculation ? (
            <Box display="flex" justifyContent="space-between" borderBottom={1} borderColor="divider" sx={{ pb: 2 }}>
              <Typography variant="body2">
                {t('subscription.organization.planPage.subscriptionCheckoutVatCountry', { country: vatCountryDisplay })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('subscription.organization.planPage.subscriptionCheckoutVatAtPayment')}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" justifyContent="space-between" sx={{ pb: 2 }}>
              <Typography variant="body2">{t('event.checkoutModal.noVatExplanation')}</Typography>
            </Box>
          )}
        </>
      )}
    </>
  );
}
